import RouteChoice from "../../model-factory/basic/route/RouteChoice";
import {JSX} from "react/jsx-runtime";
import {
    cloneObject,
    isArray,
    isNativeObject,
    isNumeric,
    isString,
    requireDate,
    slugToID
} from "../../common/SiteHelper";
import {PostType} from "../../manager/post/PostManager";
import {getManager} from "../../manager/Manager";
import {getModelFactory} from "../../model-factory/ModelFactory";
import {PostDataChoice} from "./PostDataFactory";
import {IPostFieldConfigItems} from "../../manager/config/PostFieldConfig";
import {IAnyObject} from "../../common/SimpleInterface";
import FileVO from "../../common/FileVO";
import ImageVO from "../../common/ImageVO";
import MapLocationVO from "../../../tsx/component/form/model/vo/MapLocationVO";
import moment from "moment/moment";
import SimpleChoice from "../../common/SimpleChoiceVO";
import FileReplaceVO, {ImageFileReplaceVO} from "../../../tsx/component/form/model/vo/FileReplaceVO";

type AcfLayout =
    "wysiwyg_layout" |
    "full_size_image_layout" |
    "multimedia_layout" |
    "gallery_layout" |
    "callout_layout" |
    "current_campaign_layout" |
    "posts_layout" |
    "custom_layout";

type BlockLayout =
    "cards" |
    "slideshow";

type BlockAlignment =
    "text-center";

type LoggedInState =
    "any" |
    "logged_in" |
    "logged_out";

type OrganisationState =
    "any" |
    "has_organisation" |
    "no_organisation";


export interface IContentBlock
{
    acfFcLayout:AcfLayout
    colour?:string
    marginAbove?:string
    marginBelow?:string
    paddingAbove?:string
    paddingBelow?:string
    innerBG?:string
    alignment?:BlockAlignment
    anchor?:string
    narrowLayout?:boolean
    title?:string
    introduction?:string
    content?:string
    hasCtas?:string
    ctas?:RouteChoice[]
    ctasTitle?:string
    layout?:BlockLayout
    desktopColumns?:number
    rotateColours?:boolean
    chosenPostType?:string
    chosenCategory?:string
    posts?:RouteChoice[]
    numberOf?:number
    hasMoreButton?:boolean
    moreButton?:string
    moreButtonCopy?:string
    internalLink?:string
    externalLink?:string
    buttonCopy?:string
    children?:JSX.Element
    showForLoggedInState?:LoggedInState
    showForOrganisationState?:OrganisationState
}

const getRouteChoiceList = (from:any):RouteChoice[] => {
    const routeChoiceList:RouteChoice[] = [];
    if(isArray(from)){
        for(let entry of from){
            const routeChoice = getRouteChoice(entry);
            if(routeChoice){
                routeChoiceList.push(routeChoice);
            }
        }
    }
    return routeChoiceList;
}

const getRouteChoice = (from:any):RouteChoice|null => {
    if(!isNativeObject(from)){
        return null;
    }

    const pageID = slugToID(from.post_name ?? "");
    if(!pageID){
        return null;
    }
    const postConfigs = getManager().post.config;
    for(let checkPostType in postConfigs){
        const postConfig = postConfigs[checkPostType as PostType];
        if(pageID === postConfig.addPageID){
            if(postConfig.type === "organisation"){
                return getModelFactory().myProfileRoute.getDefaultModel().choices.add ?? null;
            } else {
                return getModelFactory().postRoute.getDefaultModel(postConfig.type)?.choices.add ?? null;
            }
        }
    }

    for(let choice of getModelFactory().browseRoute.getDefaultModel().choiceList()){
        if(choice.postDataChoice?.getPageID() === pageID){
            return choice;
        }
    }
    return null;
}

export const filterContentBlocks = (contentBlocks:IContentBlock[]):IContentBlock[] =>
{
    const filteredContentBlocks:IContentBlock[] = [];
    const loggedIn = getManager().user.loggedIn();
    const hasOrganisation = loggedIn && !!getManager().user.organisationID;
    for(let block of contentBlocks){
        if(block.showForLoggedInState === "logged_in"){
            if(!loggedIn){
                continue;
            }
        } else if(block.showForLoggedInState === "logged_out"){
            if(loggedIn){
                continue;
            }
        } else if(block.showForOrganisationState === "has_organisation"){
            if(!hasOrganisation){
                continue;
            }
        } else if(block.showForOrganisationState === "no_organisation"){
            if(loggedIn){
                continue;
            }
        }
        filteredContentBlocks.push(block);
    }

    return filteredContentBlocks;
}

export const parseContentBlocks = (fromBlocksArray:any[]) =>
{
    const contentBlocks:IContentBlock[] = [];

    const known = [
        'acf_fc_layout',
        'colour',
        'margin_above',
        'margin_below',
        'padding_above',
        'padding_below',
        'inner_bg',
        'alignment',
        'anchor',
        'narrow_layout',
        'title',
        'introduction',
        'content',
        'has_ctas',
        'ctas',
        'ctas_title',
        'layout',
        'desktop_columns',
        'rotate_colours',
        'post_choice',
        'chosen_post_type',
        'chosen_category',
        'posts',
        'numberOf',
        'has_more_button',
        'more_button',
        'more_button_copy'
    ]

    for(let fromBlock of fromBlocksArray){
        if(!isNativeObject(fromBlock)){
            continue;
        }
        const acfFcLayout  = fromBlock.acf_fc_layout ?? null;
        if(!acfFcLayout){
            continue
        }
        const showForLoggedInState:LoggedInState|"" = (fromBlock.show_for_logged_in_state ?? "").toLowerCase();
        const showForOrganisationState:OrganisationState|"" = (fromBlock.show_for_organisation_state ?? "").toLowerCase();

        contentBlocks.push({
            acfFcLayout : acfFcLayout,
            colour: fromBlock.colour ?? "",
            marginAbove: fromBlock.margin_above ?? "",
            marginBelow: fromBlock.margin_below ?? "",
            paddingAbove: fromBlock.padding_above ?? "",
            paddingBelow: fromBlock.padding_below ?? "",
            innerBG: fromBlock.inner_bg ?? "",
            alignment: fromBlock.alignment ?? "",
            anchor: fromBlock.anchor ?? "",
            narrowLayout: fromBlock.narrow_layout ?? "",
            title: fromBlock.title ?? "",
            introduction: fromBlock.introduction ?? "",
            content: fromBlock.content ?? "",
            hasCtas: fromBlock.has_ctas ?? "",
            //ctas: fromBlock.ctas ?? "",
            ctasTitle: fromBlock.ctas_title ?? "",
            layout: fromBlock.layout ?? "",
            desktopColumns: !isNaN(fromBlock.desktop_columns) ? parseInt(fromBlock.desktop_columns) : 0,
            rotateColours: !!(fromBlock.rotate_colours ?? false),
            //postChoice: fromBlock.post_choice ?? null,
            chosenPostType: fromBlock.chosen_post_type ?? "",
            chosenCategory: fromBlock.chosen_category ?? "",
            posts: getRouteChoiceList(fromBlock.posts),
            numberOf: !isNaN(fromBlock.numberOf) ? parseInt(fromBlock.numberOf) : 0,
            hasMoreButton: !!(fromBlock.has_more_button ?? false),
            moreButton: fromBlock.more_button ?? "",
            moreButtonCopy: fromBlock.more_button_copy ?? "",
            showForLoggedInState: ((showForLoggedInState === "logged_in") || (showForLoggedInState === "logged_out")) ? showForLoggedInState : "any",
            showForOrganisationState: ((showForOrganisationState === "has_organisation") || (showForOrganisationState === "no_organisation")) ? showForOrganisationState : "any"
        })
    }
    return contentBlocks;
}

export const applyConfigToPostAcfMeta = (postType:PostType|null, acfMeta:IAnyObject) =>
{
    if(!postType){
        return;
    }
    const postFieldConfig = getManager().post.postFieldConfig[postType];

    const applyConfig = (value:any, name:string, configItems:IPostFieldConfigItems) => {
        const configItem = configItems[name];

        if(!configItem){
            return value;
        }
        if(configItem.fieldType === "group"){
            if(!isNativeObject(value)){
                return {};
            }
            const object = value as IAnyObject;
            for(let key in object){
                const subValue = object[key];
                object[key] = applyConfig(subValue, key, configItem.subfields);
            }
            return object;
        }
        if(configItem.fieldType === "repeater"){
            if(!isArray(value)){
                return [];
            }
            const array = value as any[];
            for(let i = 0; i < array.length; i++){
                const subValue = array[i];
                if(isNativeObject(subValue)){
                    const object = subValue as IAnyObject;
                    for(let key in object){
                        const subSubValue = object[key];
                        object[key] = applyConfig(subSubValue, key, configItem.subfields);
                    }
                }
            }
            return array;
        }
        if(configItem.fieldType === "file"){
            if(!isArray(value)){
                return [];
            }
            const array = value as any[];

            for(let i = 0; i < array.length; i++){
                const subValue = array[i];
                const object = isNativeObject(subValue) ? subValue as IAnyObject : {};
                const fileVO = new FileVO(object);
                if(!fileVO.valid){
                    array.splice(i, 1);
                    i --;
                    continue;
                }
                array[i] = fileVO;
            }
        }
        if(configItem.fieldType === "image"){
            if(!isArray(value)){
                return [];
            }
            const array = value as any[];

            for(let i = 0; i < array.length; i++){
                const subValue = array[i];
                const object = isNativeObject(subValue) ? subValue as IAnyObject : {};
                const imageVO = new ImageVO(object);
                if(!imageVO.valid){
                    array.splice(i, 1);
                    i --;
                    continue;
                }
                array[i] = imageVO;
            }
        }
        if(configItem.fieldType === "map_location"){
            if(!isNativeObject(value)){
                return null;
            }
            if(isNaN(value.lat) || isNaN(value.lng)){
                return null;
            }
            const latitude = parseFloat(value.lat);
            const longitude = parseFloat(value.lng);
            const boundsWidth = parseFloat(value.bounds_width ?? 0);
            const boundsHeight = parseFloat(value.bounds_height ?? 0);

            const mapLocationVO  = new MapLocationVO(latitude, longitude)
            let zoom = value.zoom ? parseInt(value.zoom) : null;
            if(zoom){
                zoom = Math.min(zoom, 10)
            }

            mapLocationVO.boundsWidth = boundsWidth;
            mapLocationVO.boundsHeight = boundsHeight;
            mapLocationVO.address = value.address ?? null;
            mapLocationVO.zoom = zoom;
            mapLocationVO.placeID = value.place_id ? parseInt(value.place_id) : null;
            mapLocationVO.city = value.city ?? null;
            mapLocationVO.state = value.state ?? null;
            mapLocationVO.postcode = value.post_code ?? null;
            mapLocationVO.country = value.country ?? null;
            mapLocationVO.countryShort = value.country_short ?? null;

            value = mapLocationVO;
        }
        return value;
    }

    const configItems = postFieldConfig.items;
    for(let name in acfMeta){
        const value = acfMeta[name];
        acfMeta[name] = applyConfig(value, name, configItems)
    }
}

export const getPostSubmitValues = (postType:PostType|null, acfMeta:IAnyObject):IAnyObject =>
{

    const getSubmitValue = (value:any, name:string, configItems:IPostFieldConfigItems) => {
        const configItem = configItems[name];

        if(!configItem || configItem.hiddenFromSubmit){
            return null;
        }
        if(configItem.fieldType === "group"){
            if(!isNativeObject(value)){
                return {};
            }
            const object = JSON.parse(JSON.stringify(value)) as IAnyObject;
            for(let key in object){
                const subValue = object[key];
                object[key] = getSubmitValue(subValue, key, configItem.subfields);
            }
            return object;
        }
        if(configItem.fieldType === "repeater"){
            if(!isArray(value)){
                return [];
            }
            const array = JSON.parse(JSON.stringify(value)) as any[];
            for(let i = 0; i < array.length; i++){
                const subValue = array[i];
                if(isNativeObject(subValue)){
                    const object = subValue as IAnyObject;
                    for(let key in object){
                        const subSubValue = object[key];
                        object[key] = getSubmitValue(subSubValue, key, configItem.subfields);
                    }
                }
            }
            return array;
        }
        if(configItem.fieldType === "file"){
            return new FileReplaceVO(isArray(value) ? value : []);
        }
        if(configItem.fieldType === "image"){
            return new ImageFileReplaceVO(isArray(value) ? value : []);
        }
        if(configItem.fieldType === "map_location"){
            value = value instanceof MapLocationVO ? value.clone() : new MapLocationVO();
        }
        return value;
    }
    let submitValues:IAnyObject = {};
    const postFieldConfig = postType ? getManager().post.postFieldConfig[postType] : null;
    if(!postFieldConfig){
        return submitValues;
    }

    const configItems = postFieldConfig.items;
    for(let name in acfMeta){
        let value = acfMeta[name];
        value = getSubmitValue(value, name, configItems);
        if(value !== null){
            submitValues[name] = value;
        }
    }
    return submitValues;
}

export const getPostVisibleMeta = (postType:PostType|null, acfMeta:IAnyObject, ignoreConditionalFieldNames:string[] = []):IAnyObject =>
{
    const repeaterPaths:(string|(string|number)[]|any)[][] = [];
    const keyValues:IAnyObject = {};

    const getVisibleMetaValue = (value:any, name:string, configItems:IPostFieldConfigItems, itemsByKey:IPostFieldConfigItems, collectOnly:boolean, path:(string|(string|number)[])[] = []):any|null => {
        const configItem = configItems[name];


        if(!configItem){
            return value;
        }
        let fromRepeater = false;
        for(let pathItem of path){
            if(Array.isArray(pathItem)){
                fromRepeater = true;
            }
        }
        let repeaterPath:(string|(string|number)[]|any)[] = [];

        if(!fromRepeater){
            delete keyValues[configItem.key]
        } else {
            repeaterPath = path.slice();
            repeaterPath.push(configItem.key)
            repeaterPath.push(value)

            let replaceIndex = -1;
            for(let i = 0; i < repeaterPaths.length - 1; i++)
            {
                const checkPath = repeaterPaths[i];
                if(checkPath.length !== repeaterPath.length){
                    continue;
                }
                let match = false;
                for(let j = 0; j < checkPath.length - 1; j++){
                    if(isArray(checkPath[j])){
                        if(!isArray(repeaterPath[j]) || (checkPath[j][0] !== repeaterPath[j][0]) || (checkPath[j][1] !== repeaterPath[j][1])){
                            break;
                        }
                        continue;
                    }
                    if(checkPath[j] !== repeaterPath[j]){
                        break;
                    }
                    if(j === (checkPath.length - 2)){
                        match = true;
                        break;
                    }
                }
                if(match){
                    replaceIndex = i;
                    break
                }
            }
            if(replaceIndex >= 0){
                repeaterPaths.splice(replaceIndex, 1)
            }
        }

        if((value ?? null) === null){
            return null;
        }
        if(configItem.fieldType === "repeater"){
            if(!isArray(value)){
                return null;
            }
            const array = value as any[];
            for(let i = 0; i < array.length; i++){
                let subValue = array[i];
                if(isNativeObject(subValue)){
                    const object = subValue as IAnyObject;
                    for(let subSubName in object){
                        let subSubValue = object[subSubName];
                        const subSubPath = path.slice();
                        subSubPath.push([configItem.key, i])
                        subSubValue = getVisibleMetaValue(subSubValue, subSubName, configItem.subfields, itemsByKey, collectOnly, subSubPath);
                        if(subSubValue === null){
                            delete object[subSubName];
                            continue;
                        }
                        object[subSubName] = subSubValue;
                    }
                } else {
                    subValue = {};
                }
                if(!Object.keys(subValue).length ){
                    array.splice(i, 1);
                    i --;
                }

            }
            if(!array.length){
                return null;
            }
        } else if(configItem.fieldType === "group"){
            if(!isNativeObject(value)){
                return null;
            }
            const object = value as IAnyObject;
            for(let subName in object){
                let subValue = object[subName];
                const subPath = path.slice();
                subPath.push(configItem.key)
                subValue = getVisibleMetaValue(subValue, subName, configItem.subfields, itemsByKey, collectOnly,subPath);
                if(subValue === null){
                    delete object[subName];
                    continue;
                }
                object[subName] = subValue;

            }
            if(!Object.keys(object).length){
                return null;
            }
        }

        if(!fromRepeater){
            keyValues[configItem.key] = value;
        } else {
            repeaterPaths.push(repeaterPath)
        }
        if(collectOnly){
            return value;
        }

        const getCompareValue = (forKey:string):any => {
            if(!fromRepeater){
                return keyValues[forKey] ?? null;
            }
            for(let checkPath of repeaterPaths){
                for(let i = 0; i < repeaterPath.length - 1; i++)
                {
                    const myValue = repeaterPath[i];
                    const checkPathValue = i < checkPath.length - 1 ? (checkPath[i] ?? null) : null;
                    if(checkPathValue === null){
                        break;
                    }
                    if(isArray(myValue)){
                        if(!isArray(checkPathValue) || (myValue[0] !== checkPathValue[0]) || (myValue[1] !== checkPathValue[1])){
                            break;
                        }
                        continue;
                    } else if(isArray(checkPathValue)){
                        break;
                    }
                    if((checkPathValue === forKey) && (i === (checkPath.length - 2))){
                        return checkPath[i + 1];
                    }
                }
            }
            return null;
        }

        for(let conditionalLogicGroup of configItem.conditionalLogic)
        {
            for (let conditionalLogicItem of conditionalLogicGroup.items)
            {
                const compareConfigItem = itemsByKey[conditionalLogicItem.key] ?? null;
                if(!compareConfigItem){
                    continue;
                }
                if(ignoreConditionalFieldNames.includes(compareConfigItem.name)){
                    continue;
                }
                let conditionalValue = conditionalLogicItem.value ?? "";
                let compareValue = getCompareValue(conditionalLogicItem.key) ?? "";
                if(compareConfigItem.formItemType === "boolean"){
                    conditionalValue = !!(isNumeric(conditionalValue) ? parseInt(conditionalValue) : conditionalValue);
                    compareValue = !!(isNumeric(compareValue) ? parseInt(compareValue) : compareValue);
                } else if(compareConfigItem.formItemType === "int"){
                    conditionalValue = !isNaN(conditionalValue) ? parseInt(conditionalValue) : null;
                    compareValue = !isNaN(compareValue) ? parseInt(compareValue) : null;
                } else if(compareConfigItem.formItemType === "float"){
                    conditionalValue = !isNaN(conditionalValue) ? parseFloat(conditionalValue) : null;
                    compareValue = !isNaN(compareValue) ? parseFloat(compareValue) : null;
                } else if(isNumeric(conditionalValue) && isNumeric(compareValue)){
                    conditionalValue = parseFloat(conditionalValue);
                    compareValue = parseFloat(compareValue);
                }
                if(conditionalLogicItem.operator === "=="){
                    if(conditionalValue !== compareValue){
                        return null;
                    }
                } else if(conditionalLogicItem.operator === "!="){
                    if(conditionalValue === compareValue){
                        return null;
                    }
                }
            }
        }
        return value;
    }
    const visibleMeta = cloneObject(acfMeta) as IAnyObject;
    const postFieldConfig = postType ? getManager().post.postFieldConfig[postType] : null;
    if(!postFieldConfig){
        return visibleMeta;
    }

    let collectOnly = true;
    for(let i = 0; i < 3; i++){
        for(let name in visibleMeta){
            let value = visibleMeta[name];
            value = getVisibleMetaValue(value, name, postFieldConfig.items, postFieldConfig.itemsByKey, collectOnly);
            if(value === null){
                delete visibleMeta[name];
                continue;
            }
            visibleMeta[name] = value;
        }
        collectOnly = false;
    }
    return visibleMeta;
}

export const getPostUIValues = (postType:PostType|null, visibleMeta:IAnyObject):IAnyObject =>
{

    const applyUIValues = (value:any, name:string, configItems:IPostFieldConfigItems) => {
        const configItem = configItems[name];

        if(!configItem){
            return value;
        }
        if(configItem.fieldType === "group"){
            if(!isNativeObject(value)){
                return {};
            }
            const object = value as IAnyObject;
            for(let key in object){
                const subValue = object[key];
                object[key] = applyUIValues(subValue, key, configItem.subfields);
            }
            return object;
        }
        if(configItem.fieldType === "repeater"){
            if(!isArray(value)){
                return [];
            }
            const array = value as any[];
            for(let i = 0; i < array.length; i++){
                const subValue = array[i];
                if(isNativeObject(subValue)){
                    const object = subValue as IAnyObject;
                    for(let key in object){
                        const subSubValue = object[key];
                        object[key] = applyUIValues(subSubValue, key, configItem.subfields);
                    }
                }
            }
            return array;
        }
        if(configItem.fieldType === "date_time_picker"){
            if(configItem.dateDisplayFormat){
                value = requireDate(value);
                if(value){
                    return moment(value).format(configItem.dateDisplayFormat)
                }
            } else if(isString(value)){
                return value;
            }
            return "";
        }
        if(configItem.fieldType === "checkbox"){
            if(!isArray(value)){
                value = [value];
            }
            const choiceList:SimpleChoice[] = [];
            for(let i = 0; i < value.length; i++){
                const stringValue = (value[i] ?? "").toString();
                const choice = stringValue ? (configItem.choices[stringValue] ?? null) : null;
                if(!choice){
                    continue;
                }
                choiceList.push(choice);
            }
            return choiceList;
        }
        if((configItem.fieldType === "radio") || (configItem.fieldType === "select")){
            const stringValue = (value ?? "").toString();
            const choice = stringValue ? (configItem.choices[stringValue] ?? null) : null;
            if(!choice){
                return "";
            }
            return choice;
        }
        if(configItem.fieldType === "true_false"){
            return value ? "Yes" : "No";
        }
        if(value){
            const stringValue = value.toString();
            const choice = configItem.choices[stringValue];
            if(choice){
                value = choice.name;
            }
        }

        return value;
    }
    let uiValues:IAnyObject = {};
    const postFieldConfig = postType ? getManager().post.postFieldConfig[postType] : null;
    if(!postFieldConfig){
        return uiValues;
    }

    const configItems = postFieldConfig.items;
    for(let name in visibleMeta){
        const value = visibleMeta[name];
        uiValues[name] = applyUIValues(value, name, configItems)
    }
    return uiValues;
}
